import { Scene, GameObjects } from 'phaser';
import * as Phaser from 'phaser';
import { initLocalization, t } from '../i18n/Localization';
import { Player } from '../entities/Player';

export class Tutorial extends Scene {
  private background!: GameObjects.Image;
  private panel!: GameObjects.Rectangle;
  private player!: Player;
  private escKey!: Phaser.Input.Keyboard.Key;
  private goal!: Phaser.Physics.Arcade.Image;
  private exitZoneSize: number = 80;
  private isCompleted: boolean = false;

  constructor() {
    super('Tutorial');
  }

  create(): void {
    initLocalization(this);

    const { width, height } = this.scale;

    this.createBackground(width, height);
    this.createUI(width, height);
    this.createPlayer(width, height);
    this.createGoal(width, height);
    this.createExit(width, height);
    this.setupInput();
  }

  // 🎨 Fondo animado
  private createBackground(width: number, height: number) {
    const centerX = width / 2;
    const centerY = height / 2;

    this.background = this.add.image(centerX, centerY, 'menu_v2_bg');

    const scale = Math.max(width / this.background.width, height / this.background.height);
    this.background.setScale(scale);

    this.tweens.add({
      targets: this.background,
      scale: scale * 1.05,
      duration: 4000,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut'
    });
  }

  // 🧱 UI
  private createUI(width: number, height: number) {
    const cx = width / 2;
    const cy = height / 2;

    this.panel = this.add.rectangle(cx, cy, width * 0.75, height * 0.65, 0x0b0f16, 0.8)
      .setStrokeStyle(2, 0x3a4a66, 0.9);

    this.add.text(cx, cy - height * 0.28, t('tutorial.title'), {
      fontSize: `${Math.floor(height * 0.05)}px`,
      color: '#fff',
      stroke: '#0b0f16',
      strokeThickness: 6,
    }).setOrigin(0.5);

    this.add.text(cx, cy - height * 0.20, t('tutorial.subtitle'), {
      fontSize: `${Math.floor(height * 0.025)}px`,
      color: '#cfe8ff',
    }).setOrigin(0.5);

    this.add.text(cx, cy - 20, t('tutorial.controls'), {
      fontSize: '20px',
      color: '#a8d0ff',
      align: 'center'
    }).setOrigin(0.5);

    this.add.text(cx, cy + 20, 'W A S D  /  ↑ ↓ ← →', {
      fontSize: '26px',
      color: '#fff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
  }

  // 🚀 Player
  private createPlayer(width: number, height: number) {
    this.physics.world.setBounds(0, 0, width, height);

    this.player = new Player(this, width / 2, height * 0.7, {
      enableWeapons: false,
      maxSpeed: 420,
      acceleration: 900,
      deceleration: 900,
    });
  }

  // 🎯 Goal con physics (FIX IMPORTANTE)
  private createGoal(width: number, height: number) {
    const x = width / 2;
    const y = height * 0.3;

    // Usar textura global existente (evita key faltante en runtime)
    this.goal = this.physics.add.image(x, y, 'hud_biomoney');
    this.goal.setScale(0.6);
    this.goal.setImmovable(true);

    this.tweens.add({
      targets: this.goal,
      scale: 0.7,
      yoyo: true,
      repeat: -1,
      duration: 600
    });

    this.physics.add.overlap(this.player, this.goal, this.onReachGoal, undefined, this);
  }

  private onReachGoal() {
    if (this.isCompleted) return;

    this.isCompleted = true;

    this.goal.destroy();

    // ✨ feedback pro
    this.cameras.main.flash(300, 0, 255, 200);

    this.add.text(this.scale.width / 2, this.scale.height * 0.75, t('tutorial.tip'), {
      fontSize: '22px',
      color: '#00ffcc',
    }).setOrigin(0.5);
  }

  // 🚪 Salida
  private createExit(width: number, height: number) {
    const zone = this.add.rectangle(
      0,
      height,
      this.exitZoneSize,
      this.exitZoneSize,
      0xff4d4d,
      0.25
    ).setOrigin(0, 1);

    this.tweens.add({
      targets: zone,
      alpha: 0.1,
      yoyo: true,
      repeat: -1,
      duration: 800
    });

    this.add.text(10, height - 10, 'EXIT (ESC)', {
      fontSize: '14px',
      color: '#ffaaaa'
    }).setOrigin(0, 1);
  }

  // ⌨️ Input
  private setupInput() {
    if (this.input.keyboard) {
      this.escKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
    }
  }

  update(): void {
    if (!this.player) return;

    const inExitCorner =
      this.player.x <= this.exitZoneSize &&
      this.player.y >= this.scale.height - this.exitZoneSize;

    if (inExitCorner || (this.escKey && Phaser.Input.Keyboard.JustDown(this.escKey))) {
      this.scene.start('MainMenu');
    }
  }
}
